// frontend/src/hooks/useAuth.ts

import { useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store'
import toast from 'react-hot-toast'  // Используем react-hot-toast напрямую
import { apiClient } from '@/api/client'
import type { LoginCredentials, RegisterData, User } from '@/types'

export const useAuth = () => {
  const navigate = useNavigate()
  
  const {
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    isLoading,
    login: storeLogin,
    register: storeRegister,
    logout: storeLogout,
    setTokens,
    setUser,
    updateUser: storeUpdateUser,
    refreshUser: storeRefreshUser,
  } = useAuthStore()

  const login = useCallback(
    async (credentials: LoginCredentials, redirectTo?: string) => {
      try {
        await storeLogin(credentials)
        toast.success('Добро пожаловать!')
        
        if (redirectTo) {
          navigate(redirectTo)
        } else {
          navigate('/dashboard')
        }
        return true
      } catch (error: any) {
        toast.error(
          error.response?.data?.detail || 'Ошибка входа'
        )
        return false
      }
    },
    [storeLogin, navigate]
  )

  const register = useCallback(
    async (data: RegisterData, redirectTo?: string) => {
      try {
        await storeRegister(data)
        toast.success('Регистрация прошла успешно!')
        
        if (redirectTo) {
          navigate(redirectTo)
        } else {
          navigate('/dashboard')
        }
        return true
      } catch (error: any) {
        toast.error(
          error.response?.data?.detail || 'Ошибка регистрации'
        )
        return false
      }
    },
    [storeRegister, navigate]
  )

  const logout = useCallback(
    async (redirectTo?: string) => {
      try {
        await apiClient.post('/v1/auth/logout')
      } catch (error) {
        // Игнорируем ошибки при логауте
      } finally {
        storeLogout()
        toast.success('Вы вышли из системы')
        
        if (redirectTo) {
          navigate(redirectTo)
        } else {
          navigate('/login')
        }
      }
    },
    [storeLogout, navigate]
  )

  const updateUser = useCallback(
    async (data: Partial<User>) => {
      try {
        await storeUpdateUser(data)
        toast.success('Профиль обновлен')
        return true
      } catch (error: any) {
        toast.error(
          error.response?.data?.detail || 'Ошибка обновления профиля'
        )
        return false
      }
    },
    [storeUpdateUser]
  )

  const refreshAuth = useCallback(async () => {
    try {
      await storeRefreshUser()
      return true
    } catch (error) {
      await logout()
      return false
    }
  }, [storeRefreshUser, logout])

  const hasRole = useCallback(
    (roles: string | string[]) => {
      if (!user) return false
      
      const roleArray = Array.isArray(roles) ? roles : [roles]
      return roleArray.includes(user.role)
    },
    [user]
  )

  const isEmployer = useMemo(() => hasRole(['employer', 'admin']), [hasRole])
  const isAdmin = useMemo(() => hasRole('admin'), [hasRole])
  const isCandidate = useMemo(() => hasRole('candidate'), [hasRole])

  useEffect(() => {
    if (isAuthenticated && !user) {
      storeRefreshUser()
    }
  }, [isAuthenticated, user, storeRefreshUser])

  return {
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    isLoading,
    isEmployer,
    isAdmin,
    isCandidate,
    login,
    register,
    logout,
    updateUser,
    refreshAuth,
    hasRole,
    setTokens,
    setUser,
  }
}